import { ArrowRight } from "lucide-react";
import ParallaxSection from "@/components/ParallaxSection";
import { useBooking } from "@/context/BookingContext";
import {
  doubleHeightLoungeImg, poolImg, spaImg, gymImg,
  orchidPatternImg, logoSymbol, IconGlyph,
  theVandaBuildingImg, atriumReceptionImg, restaurantImg,
} from "@/data";
import staffServiceImg from "@/assets/hotel/staff.png";
import turaLocationImg from "@/assets/hotel/tura-location.jpeg";
import diningTeaserBgImg from "@/assets/hotel/dining-teaser-bg.webp";
import barImg from "@/assets/dining/setting-4.png";

const amenityCards = [
  {
    title: "The Orchid Spa Retreat",
    desc: "Warm stone, quiet hands, and holistic rituals inspired by the hills of Meghalaya.",
    img: spaImg,
    icon: "lotus",
  },
  {
    title: "The Summit Fitness Studio",
    desc: "Floor-to-ceiling light and modern equipment — a space built for movement, not just machines.",
    img: gymImg,
    icon: "dumbbell",
  },
  {
    title: "The Lounge",
    desc: "Warm textures and floor-to-ceiling glass — a calm space to unwind, whether between meetings or a day exploring Tura.",
    img: doubleHeightLoungeImg,
    icon: "sofa",
  },
];
import logoSymbolOffWhite from "@/assets/logo/TheVanda_Logo_Symbol_OffWhite.svg";
import rakeshImg from "@/assets/founder/rakesh.png";
import aboutBgImg from "@/assets/hotel/about-bg.png";
import nextChapterImg from "@/assets/hotel/next-chapter.png";
import founderBgImg from "@/assets/hotel/founder-bg.png";
import ideaExpImg from "@/assets/hotel/idea-exp.png";
import ideaSpaImg from "@/assets/hotel/idea-spa.png";
import aboutHeroImg from "@/assets/hotel/about-hero.png";

export default function About() {
  const { openBooking } = useBooking();

  return (
    <>
      {/* HERO */}
      <ParallaxSection image={aboutHeroImg} className="ab-hero" strength={14}>
        <div className="ab-hero-text">
          <div className="ab-marker">
            <p className="eyebrow">
              <ArrowRight size={13} /> About Us
            </p>
          </div>
          <h1>
            The Vanda Hotel is not an introduction to Tura — <em>it is a new way to experience it.</em>
          </h1>
          <img src={logoSymbolOffWhite} alt="" aria-hidden="true" className="ab-hero-mark" />
        </div>
      </ParallaxSection>

      {/* STATEMENT — Tura's Finest positioning */}
      <section className="ab-statement">
        <p className="ab-statement-eyebrow">Tura's Finest</p>
        <h2>
          A new benchmark in <em>business &amp; leisure travel.</em>
        </h2>
      </section>

      {/* A SENSE OF PLACE — intro */}
      <div className="ab-intro" style={{ backgroundImage: `url(${aboutBgImg})` }}>
        <div className="ab-intro-inner">
          <div className="ab-marker on-light">
            <p className="ab-marker-num">01</p>
            <span className="ab-marker-line" aria-hidden="true" />
            <p className="ab-marker-label">A Sense of Place</p>
          </div>
          <p className="fade-up">
            There is a feeling of discovering something uncommon: an invitation to experience design shaped by nature, and warm service rooted in local pride.
          </p>
        </div>
      </div>

      {/* LOCATION & ACCESS — Tura landscape banner + getting-here stats */}
      <section className="ab-location" style={{ backgroundImage: `url(${turaLocationImg})` }}>
        <div className="ab-location-overlay" aria-hidden="true" />
        <div className="ab-location-inner">
          <div className="ab-location-copy fade-up">
            <p className="eyebrow light center">
              <ArrowRight size={13} /> Location &amp; Access
            </p>
            <h2>
              Set high in the <em>Garo Hills.</em>
            </h2>
            <p>
              The Vanda sits in the heart of Tura, Meghalaya — a hill town of pine-covered ridges, waterfalls and quiet valleys, with Tura Peak rising just beyond the property. A destination in its own right, long before you reach the door.
            </p>
            <p>
              As Tura's leading business and leisure hotel, The Vanda is centrally placed near the town's commercial district, government offices and main market — as convenient for a boardroom as it is for a weekend away.
            </p>
            <div className="ab-location-stats">
              <div>
                <strong>Central</strong>
                <span>Tura's commercial &amp; business district</span>
              </div>
              <div>
                <strong>≈ 220 km</strong>
                <span>From Guwahati</span>
              </div>
              <div>
                <strong>≈ 5–6 hrs</strong>
                <span>By road from Guwahati</span>
              </div>
              <div>
                <strong>GAU</strong>
                <span>Nearest airport — Guwahati</span>
              </div>
              <div>
                <strong>GHY</strong>
                <span>Nearest railway station — Guwahati</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE ARCHITECTURE — design inspiration */}
      <div className="split-band ab-architecture reverse">
        <div className="ab-accent-media">
          <img src={theVandaBuildingImg} alt="The Vanda hotel exterior in Tura" />
          <img src={atriumReceptionImg} alt="Atrium reception at The Vanda" className="ab-accent-corner" />
        </div>
        <div className="split-band-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> The Architecture
          </p>
          <h2>
            Built from the <em>hills themselves.</em>
          </h2>
          <p>
            Stone, timber and warm concrete take their cues from the Garo Hills that surround them. A central atrium draws in natural light and air, blurring the line between the indoors and the landscape beyond — modern comfort, built the way Tura builds.
          </p>
        </div>
      </div>

      {/* THE VANDA EXPERIENCE — image with small overlapping accent */}
      <div className="split-band ab-vanda-exp">
        <div className="ab-accent-media">
          <img src={doubleHeightLoungeImg} alt="Double height lounge at The Vanda" />
          <img src={orchidPatternImg} alt="" aria-hidden="true" className="ab-accent-corner" />
        </div>
        <div className="split-band-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> The Vanda Experience
          </p>
          <h2>
            It simply belongs, <em>so completely.</em>
          </h2>
          <p>
            Built with intention and held to a personal standard, The Vanda reflects a belief that hospitality here can be thoughtful, consistent and quietly precise. Like the orchid it is named after, it does not try to stand apart.
          </p>
        </div>
      </div>

      {/* SIGNATURE AMENITIES — More Than a Stay, A Deeper Experience */}
      <section className="ab-amenities" data-testid="ab-amenities-section">
        <div className="ab-am-inner">
          <div className="ab-am-top">
            <div className="ab-am-intro fade-up">
              <p className="eyebrow light">
                <ArrowRight size={13} /> Signature Amenities
              </p>
              <h2>
                More Than a Stay, <em>A Deeper Experience</em>
              </h2>
              <p className="ab-am-sub">
                Thoughtfully curated spaces and modern comforts, inspired by Meghalaya's natural beauty — designed for relaxation, connection and complete wellbeing.
              </p>
              <a href="/#amenities-section" className="ab-am-explore">
                <span className="ab-am-explore-icon">
                  <ArrowRight size={16} />
                </span>
                Explore All Amenities
              </a>
            </div>

            <div className="ab-am-feature-media fade-up">
              <img src={poolImg} alt="Infinity pool at The Vanda" />
            </div>

            <div className="ab-am-feature-copy fade-up">
              <p className="ab-am-feature-num">01</p>
              <h3>Infinity Pool</h3>
              <p>
                An infinity pool that blurs into the hills — a serene plunge above the town, perfect for a refreshing pause. The water's edge appears to dissolve into the Garo Hills beyond, framing an uninterrupted view of Tura from the moment you step in.
              </p>
              <p>
                Open through the day, it's as suited to a quiet morning lap as it is to watching the light change over the hills come evening — with loungers, shade and attentive service close at hand throughout.
              </p>
            </div>
          </div>

          <div className="ab-am-grid">
            {amenityCards.map((a) => (
              <div className="ab-am-card fade-up" key={a.title}>
                <div className="ab-am-card-media">
                  <img src={a.img} alt={a.title} loading="lazy" />
                </div>
                <div className="ab-am-card-copy">
                  <span className="ab-am-card-icon">
                    <IconGlyph name={a.icon} />
                  </span>
                  <h4>{a.title}</h4>
                  <p>{a.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="ab-am-foot">
            <p>Nature / Comfort / Wellbeing / You</p>
            <p>Curated for meaningful stays</p>
          </div>
        </div>
      </section>

      {/* DINING TEASER — links through to the Dining page */}
      <section className="ab-dining-teaser" style={{ backgroundImage: `url(${diningTeaserBgImg})` }}>
        <div className="ab-dining-teaser-copy fade-up">
          <p className="eyebrow light center">
            <ArrowRight size={13} /> Dining
          </p>
          <h2>
            Food that's a reason to stay, <em>not just an amenity.</em>
          </h2>
          <p>
            The Orchid Terrace moves easily across Continental, North Indian, Pan-Asian and local Garo &amp; Khasi kitchens — with The Vanda Bar carrying the evening on, above the town.
          </p>
          <a href="/dining" className="btn btn-outline light" data-testid="about-dining-link">
            Explore Dining <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* THE VANDA BAR — image collage with text alongside */}
      <div className="split-band ab-bar-section">
        <div className="ab-accent-media">
          <img src={barImg} alt="Rooftop bar seating at The Vanda, at dusk" />
          <img src={restaurantImg} alt="The Orchid Terrace restaurant interior" className="ab-accent-corner" />
        </div>
        <div className="split-band-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> The Vanda Bar
          </p>
          <h2>
            Good spirits, <em>above the town.</em>
          </h2>
          <p>
            The Vanda Bar pairs a curated list of spirits and craft cocktails with sweeping views of the Garo Hills — a fitting close to a day in Tura, right alongside The Orchid Terrace's kitchen.
          </p>
          <a href="/dining" className="btn btn-outline" data-testid="about-bar-link">
            Explore Dining <ArrowRight size={15} />
          </a>
        </div>
      </div>

      {/* THE IDEA — staggered editorial layout, image on alternating sides */}
      <section className="ab-idea">
        <div className="ab-idea-grid">
          <div className="ab-idea-col">
            <img src={ideaExpImg} alt="Experience at The Vanda" className="ab-idea-img fade-up" />
            <div className="ab-idea-text fade-up">
              <p>
                Every detail is a fitting expression of the story the architecture is telling. Nothing feels added or forced. Luxury emerges effortlessly from authenticity. Global in standard. Yet deeply belongs to the place.
              </p>
              <p className="ab-idea-signature">Refined. Rooted. Rare.</p>
            </div>
          </div>
          <div className="ab-idea-col right">
            <div className="ab-idea-text fade-up">
              <p className="eyebrow">
                <ArrowRight size={13} /> The Idea
              </p>
              <h2>
                To create something Tura <em>had never experienced.</em>
              </h2>
              <p>
                A hotel that brings global standards of hospitality without losing its sense of place. Not a template placed onto the land, but something that belongs to it. The Vanda orchid, native to Meghalaya, became a befitting muse — rare, resilient, deeply rooted in its environment. It does not grow everywhere. But where it does, it belongs completely.
              </p>
            </div>
            <img src={ideaSpaImg} alt="Spa at The Vanda" className="ab-idea-img fade-up" />
          </div>
        </div>
      </section>

      {/* SERVICE, PERFECTED — the biggest differentiator */}
      <div className="split-band dark ab-service">
        <div className="split-band-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> Service, Perfected
          </p>
          <h2>
            The details <em>you don't have to ask for.</em>
          </h2>
          <p>
            The biggest difference at The Vanda isn't the building — it's the people in it. Every member of staff is trained to anticipate, not just respond; to remember your name by the second morning; to make sure the same warmth greets you at checkout as it did at arrival.
          </p>
          <ul className="ab-service-list">
            <li>
              <span className="ab-service-tick" />
              <p>Personally trained, warmly local</p>
            </li>
            <li>
              <span className="ab-service-tick" />
              <p>Anticipates your needs, quietly</p>
            </li>
            <li>
              <span className="ab-service-tick" />
              <p>Consistent — every stay, every time</p>
            </li>
            <li>
              <span className="ab-service-tick" />
              <p>Attentive, around the clock</p>
            </li>
          </ul>
        </div>
        <div className="split-band-media">
          <img src={staffServiceImg} alt="A member of staff at The Vanda" />
        </div>
      </div>

      {/* THE FOUNDER'S STORY — framed layered-card layout */}
      <section
        className="founder-band"
        style={{ backgroundImage: `linear-gradient(rgba(243,238,230,0.62), rgba(243,238,230,0.62)), url(${founderBgImg})` }}
      >
        <div className="founder-band-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> The Founder's Story
          </p>
          <h2>
            A question, <em>became a decision.</em>
          </h2>
          <div className="founder-quote-block">
            <p>
              Rakesh grew up in Tura, Meghalaya — not as a destination, but as home. As his travels took him across the world, he experienced some of the finest hotels, and a quiet thought stayed with him: why should Tura not have this? When he returned, the gap was clear — there were places to stay, but very few that offered care, consistency and pride in service; very few that created a sense of arrival.
            </p>
          </div>
          <span className="founder-quote-mark" aria-hidden="true">"</span>
        </div>
        <div className="founder-band-media fade-up">
          <div className="founder-photo-back" aria-hidden="true" />
          <img src={rakeshImg} alt="Rakesh, Founder of The Vanda" className="founder-photo-front" />
        </div>
      </section>

      {/* DESIGNED AS A BRIDGE — parallax quote */}
      <ParallaxSection image={doubleHeightLoungeImg} className="ab-quote-band" strength={22}>
        <h2>A place that understands why you've arrived, and adapts to it with quiet precision.</h2>
      </ParallaxSection>

      {/* CTA */}
      <section className="ab-cta" style={{ backgroundImage: `url(${nextChapterImg})` }}>
        <div className="ab-cta-inner">
          <p className="eyebrow">
            <ArrowRight size={13} /> Your next chapter
          </p>
          <h2>
            Designed as a bridge, <em>waiting to welcome you.</em>
          </h2>
          <button onClick={openBooking} className="btn btn-outline light" data-testid="about-cta-book-now">
            Book Your Stay <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </>
  );
}
